import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../../services/hrmsService";
import { EmptyState, Field, LoadState, PrimaryButton } from "../hrComponents";
import { addDaysYmd, attendanceStatus, badgeStyle, cardStyle, formatDate, iconBtnStyle, inputStyle, localTime, tdStyle, thStyle, todayYmd, useLoad } from "../hrUi";

// Marking attendance by hand, on behalf of the employees: pick the day, type the time each person came in and left,
// save. The server decides what is allowed (the date window, the attendance lock, people with punches of their own);
// this only shows what is recorded and sends what was changed. Times are the company's clock (UAE or India time).
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };
const timeInput = { ...inputStyle, width: 120 };

// "2026-10-05 08:00:00" -> "08:00" for a time input; nothing recorded -> "".
const hm = (stamp) => {
  const t = localTime(stamp);
  return t === "—" ? "" : t;
};

export default function MarkAttendanceTab() {
  const [date, setDate] = useState(todayYmd());
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({}); // employee_id -> { in, out, clear }
  const [errors, setErrors] = useState({}); // employee_id -> the server's reason
  const [saving, setSaving] = useState(false);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getMarkSheet(date), [date]);

  const firstDate = data ? (data.locked_through && data.locked_through >= data.earliest_date ? addDaysYmd(data.locked_through, 1) : data.earliest_date) : undefined;

  const changeDate = (value) => {
    if (!value) return;
    setDate(value);
    setEdits({});
    setErrors({});
  };

  const original = (row) => ({ in: hm(row.in_local), out: hm(row.out_local) });
  const valueOf = (row, key) => (edits[row.employee_id] && edits[row.employee_id][key] !== undefined ? edits[row.employee_id][key] : original(row)[key]);
  const setTime = (row, key, value) => {
    setErrors((e) => ({ ...e, [row.employee_id]: undefined }));
    setEdits((all) => ({ ...all, [row.employee_id]: { ...all[row.employee_id], [key]: value, clear: false } }));
  };
  const toggleClear = (row) => setEdits((all) => ({ ...all, [row.employee_id]: { clear: !(all[row.employee_id] && all[row.employee_id].clear) } }));

  // What would be sent: rows whose times differ from what is recorded (or that are to be removed).
  const entries = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const row of data.employees) {
      const edit = edits[row.employee_id];
      if (!edit || row.blocked) continue;
      if (edit.clear) {
        if (row.marked) out.push({ employee_id: row.employee_id, clear: true });
        continue;
      }
      const base = { in: hm(row.in_local), out: hm(row.out_local) };
      const inTime = edit.in !== undefined ? edit.in : base.in;
      const outTime = edit.out !== undefined ? edit.out : base.out;
      if (inTime === base.in && outTime === base.out) continue;
      out.push({ employee_id: row.employee_id, in_time: inTime, out_time: outTime });
    }
    return out;
  }, [data, edits]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.saveMarks({ date, entries });
      if (!res?.success) {
        toast.error(hrmsService.responseMessage(res));
        return;
      }
      const results = res.data?.results || [];
      const failed = results.filter((r) => !r.ok);
      // The rows that were saved are no longer edits; the ones refused stay, with the reason beside them.
      setEdits((all) => Object.fromEntries(Object.entries(all).filter(([id]) => failed.some((f) => String(f.employee_id) === id))));
      setErrors(Object.fromEntries(failed.map((f) => [f.employee_id, f.error])));
      if (failed.length) toast.warn(res.message || `${failed.length} not saved`);
      else toast.success(res.message || "Attendance saved");
      reload();
    } finally {
      setSaving(false);
    }
  };

  const rows = (data?.employees || []).filter((r) => {
    const q = search.trim().toLowerCase();
    return !q || r.full_name.toLowerCase().includes(q) || String(r.employee_code || "").toLowerCase().includes(q);
  });

  return (
    <>
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <Field label="Day" htmlFor="mark-date" style={{ marginBottom: 0 }}>
          <input id="mark-date" type="date" className="form-control" style={{ ...inputStyle, width: 170 }} value={date} min={firstDate} max={data?.today} onChange={(e) => changeDate(e.target.value)} />
        </Field>
        <Field label="Find a person" htmlFor="mark-search" style={{ marginBottom: 0 }}>
          <input id="mark-search" type="search" className="form-control" style={{ ...inputStyle, width: 240 }} placeholder="Name or code" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Field>
        <div className="ms-auto">
          <PrimaryButton onClick={save} disabled={saving || entries.length === 0}>
            {entries.length ? `Save ${entries.length} change${entries.length === 1 ? "" : "s"}` : "Save"}
          </PrimaryButton>
        </div>
      </div>

      {data ? (
        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>
          Times are on the company's clock ({data.timezone === "Asia/Kolkata" ? "India" : "UAE"} time). You can mark back to {formatDate(firstDate)}
          {data.locked_through ? `; attendance is locked up to ${formatDate(data.locked_through)}` : ""}. A person who punched in themselves that day can't be changed here. Use a correction request for them.
        </div>
      ) : null}

      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="Nobody to show" text={search ? "No one matches that search." : "There are no active employees for this day."} />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Employee", "In", "Out", "Recorded", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const edit = edits[r.employee_id];
                  const removing = !!(edit && edit.clear);
                  const st = r.day_status ? attendanceStatus(r.day_status) : null;
                  return (
                    <tr key={r.employee_id} style={removing ? { opacity: 0.55 } : undefined}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600 }}>{r.full_name}</div>
                        <div style={{ fontSize: 12, color: "#6B7280" }}>
                          {r.employee_code}
                          {r.department_name ? ` · ${r.department_name}` : ""}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <input type="time" className="form-control" style={timeInput} aria-label={`In time for ${r.full_name}`} value={removing ? "" : valueOf(r, "in")} disabled={!!r.blocked || removing} onChange={(e) => setTime(r, "in", e.target.value)} />
                      </td>
                      <td style={tdStyle}>
                        <input type="time" className="form-control" style={timeInput} aria-label={`Out time for ${r.full_name}`} value={removing ? "" : valueOf(r, "out")} disabled={!!r.blocked || removing} onChange={(e) => setTime(r, "out", e.target.value)} />
                      </td>
                      <td style={{ ...tdStyle, fontSize: 13 }}>
                        {errors[r.employee_id] ? (
                          <div style={{ color: "#B91C1C" }} role="alert">
                            {errors[r.employee_id]}
                          </div>
                        ) : r.blocked ? (
                          <div style={{ color: "#92400E" }}>{r.blocked}</div>
                        ) : removing ? (
                          <div style={{ color: "#991B1B" }}>Will be removed</div>
                        ) : r.marked ? (
                          <div style={{ color: "#6B7280" }}>Marked by {r.marked_by_name || "someone"}</div>
                        ) : null}
                        {st ? <span style={badgeStyle(st.bg, st.color)}>{st.label}</span> : null}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {r.marked && !r.blocked ? (
                          <button type="button" style={linkBtn} onClick={() => toggleClear(r)}>
                            {removing ? "Keep" : "Remove"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
    </>
  );
}
